// People = recipients ∪ ledger rows ∪ conversations, one Person per phone (plan review point 3).
import type { Conversation, LedgerRow, Recipient, WaError } from "@/data/types";
import { type PStatus, phoneStatus, regStatus } from "./status";

export interface Reg {
  /** ledger key (= recipient doc id) */
  key: string;
  baseKey: string;
  name: string;
  phone: string | null;
  days: string;
  dates: string;
  /** sheet sessions ("16 Morning"); [] for ledger-only rows */
  sessions: string[];
  sheetTs: string | null;
  recipient?: Recipient;
  ledger?: LedgerRow;
  /** null = ignored for status (duplicate) */
  status: PStatus | null;
  /** false = only in the ledger: left the sheet after it was messaged */
  inSheet: boolean;
  /**
   * Failed or stuck `sending`, no longer in the sheet, last touched before the snapshot was published.
   * The sender only re-claims rows of the current source (runner.build_plan), so this is never retried
   * and does not drive the person's status. See isNotRetried.
   */
  notRetried: boolean;
}

export interface Person {
  /** URL-safe id for ?p=: phone digits, or "r-<key>" when there is no valid phone */
  id: string;
  phone: string | null;
  name: string;
  regs: Reg[];
  conv?: Conversation;
  optedOut: boolean;
  status: PStatus;
  /** the registration that decides `status` (its error and times drive the detail) */
  primary?: Reg;
  /** last activity: conversation, else the latest send */
  lastAt: number | null;
  /** earliest sheet timestamp across regs, for "who is next" */
  sheetTs: string | null;
}

export interface PeopleInput {
  recipients: Recipient[];
  ledger: LedgerRow[];
  conversations: Conversation[];
  optouts: ReadonlySet<string>;
  /** when set, only conversations whose `events` contain it are used (the number is shared across events) */
  eventKey?: string;
  /** config `published_at`: when `wa publish` last wrote the recipients snapshot */
  publishedAt?: number | null;
}

/**
 * Failed/sending, not in the snapshot, and its last ledger activity is before the snapshot was published.
 * A row sent after the last publish may come from a sheet row the snapshot has not seen yet (`wa send` reads
 * the live sheet), so it stays visible. No snapshot time or no row time → not notRetried.
 */
function isNotRetried(row: LedgerRow, status: PStatus | null, publishedAt: number | null | undefined): boolean {
  if (status !== "retrying" && status !== "failed" && status !== "over_limit" && status !== "sending") return false;
  const times = [row.failedAt, row.lastAttemptAt, row.sentAt].filter((t): t is number => t != null);
  return publishedAt != null && times.length > 0 && Math.max(...times) < publishedAt;
}

/** "R1#2" → "R1" (recipient.base_of). */
export function baseOf(key: string): string {
  const i = key.lastIndexOf("#");
  if (i < 0) return key;
  return /^\d+$/.test(key.slice(i + 1)) ? key.slice(0, i) : key;
}

const phonesAgree = (a: string | null, b: string | null) => a == null || b == null || a === b;

export const personIdFor = (phone: string | null, key: string) => phone ?? `r-${key}`;

function sheetOrder(a: Reg, b: Reg): number {
  if (a.sheetTs !== b.sheetTs) {
    if (a.sheetTs == null) return 1;
    if (b.sheetTs == null) return -1;
    return a.sheetTs < b.sheetTs ? -1 : 1;
  }
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

export function peopleFromData({
  recipients,
  ledger,
  conversations: allConvs,
  optouts,
  eventKey,
  publishedAt,
}: PeopleInput): Person[] {
  const conversations = eventKey ? allConvs.filter((c) => c.events.includes(eventKey)) : allConvs;
  const byKey = new Map(ledger.map((r) => [r.key, r]));
  const used = new Set<string>();
  const regs: Reg[] = [];

  for (const rec of recipients) {
    // Doc id = resolved ledger key. Keep a phone check: keys are positional across sheet edits.
    let row = byKey.get(rec.id);
    if (row && (used.has(row.key) || !phonesAgree(row.phone, rec.phone))) row = undefined;
    if (!row && rec.phone) {
      row = ledger.find((r) => !used.has(r.key) && r.phone === rec.phone && baseOf(r.key) === rec.baseKey);
    }
    if (row) used.add(row.key);
    regs.push({
      key: row?.key ?? rec.id,
      baseKey: rec.baseKey,
      name: rec.name || row?.name || "",
      phone: rec.phone ?? row?.phone ?? null,
      days: rec.days,
      dates: rec.dates,
      sessions: rec.sessions,
      sheetTs: rec.sheetTs,
      recipient: rec,
      ledger: row,
      status: regStatus(row, rec),
      inSheet: true,
      notRetried: false,
    });
  }
  for (const row of ledger) {
    if (used.has(row.key)) continue;
    const status = regStatus(row);
    regs.push({
      key: row.key,
      baseKey: baseOf(row.key),
      name: row.name,
      phone: row.phone,
      days: row.vars.days ?? "",
      dates: row.vars.dates ?? "",
      sessions: [],
      sheetTs: null,
      ledger: row,
      status,
      inSheet: false,
      notRetried: isNotRetried(row, status, publishedAt),
    });
  }

  const groups = new Map<string, Reg[]>();
  for (const r of regs) {
    const id = personIdFor(r.phone, r.key);
    const g = groups.get(id);
    if (g) g.push(r);
    else groups.set(id, [r]);
  }
  const convByPhone = new Map(conversations.map((c) => [c.phone, c]));
  for (const c of conversations) if (!groups.has(c.phone)) groups.set(c.phone, []);

  const people: Person[] = [];
  for (const [id, group] of groups) {
    group.sort(sheetOrder);
    const phone = group[0]?.phone ?? (convByPhone.has(id) ? id : null);
    const conv = phone ? convByPhone.get(phone) : undefined;
    const optedOut = !!phone && (optouts.has(phone) || !!conv?.optedOut);
    const live = group.filter((r) => !r.notRetried);
    // A thread with no registration behind it, or only never-retried rows that left the sheet:
    // not on the list (kept out of coverage, the bar and sending).
    const statuses = live.map((r) => r.status);
    const status = live.length ? phoneStatus(statuses, optedOut) : "not_on_list";
    const primary = live.find((r) => r.status === status) ?? live[0] ?? group[0];
    const sends = group.map((r) => r.ledger?.sentAt ?? null).filter((t): t is number => t != null);
    const lastAt = conv?.lastAt ?? (sends.length ? Math.max(...sends) : null);
    people.push({
      id,
      phone,
      name: group[0]?.name || conv?.name || conv?.profileName || (phone ? `+${phone}` : id),
      regs: group,
      conv,
      optedOut,
      status,
      primary,
      lastAt,
      sheetTs: group.find((r) => r.sheetTs)?.sheetTs ?? null,
    });
  }
  return people;
}

/** Error to show on the detail: the ledger's, or the snapshot's bad-phone reason. */
export function personError(p: Person): WaError | null {
  const r = p.primary;
  if (!r) return null;
  const withError: PStatus[] = ["failed", "retrying", "invalid", "over_limit"];
  if (r.ledger?.error && withError.includes(p.status)) {
    return r.ledger.error;
  }
  if (p.status === "invalid") {
    return { code: "invalid_phone", title: "Invalid phone", detail: r.recipient?.phoneError ?? "" };
  }
  return null;
}
